import type { PassengerDetails } from "src/PassengerDetailsProvider";
import {
  SOJERN_CONSENT_PURPOSES_REQUIRED,
  SOJERN_LEGITIMATE_INTEREST_PURPOSES_REQUIRED,
  SOJERN_SALE_CONSENT_REQUIRED,
  SOJERN_SHARING_CONSENT_REQUIRED,
} from "src/data-partners/sojern/sojernPixel";
import type { InterestData, SearchResponse } from "src/api/SearchResponse";
import type { SchedulesResponse } from "src/api/SchedulesResponse";
import useSojernPixel from "src/data-partners/sojern/useSojernPixel";
import { useTravelAudience } from "src/data-partners/travelAudience/useTravelAudience";
import { useRegisterOnConsent } from "src/utils/hooks/useRegisterOnConsent";
import type { Mode } from "src/utils/types/mode";
import { useHotelsContext } from "src/domain/HotelsScreen/HotelsContext";
import useAdForm from "./adForm/useAdForm";
import { useAdara } from "./adara/useAdara";
import { useGooglePixel } from "./google/useGooglePixel";
import useStackAdapt from "./stackAdapt/useStackAdapt";

type DataPartner =
  | "Adara"
  | "AdForm"
  | "Sojern"
  | "TravelAudience"
  | "Google"
  | "StackAdapt";
export type DataPartners = DataPartner[];

export const useHotelDataPartners = (
  hotelName?: string,
  hotelStarRating?: number,
  avgDailyRate?: number,
  currencyType?: string,
) => {
  const {
    destination,
    hotelListQueryParams: { arrivalDate, departureDate },
    roomDetailsUI,
  } = useHotelsContext();

  const destinationCountryCode =
    destination && "countryCode" in destination
      ? destination.countryCode
      : undefined;

  useAdara({
    searchType: "hotel",
    hotelName,
    hotelStarRating,
    avgDailyRate,
    currencyType,
    hotelDestinationName: destination?.shortName,
    destinationCountryCode,
    hotelArrivalDate: arrivalDate,
    hotelDepartureDate: departureDate,
    roomDetails: roomDetailsUI,
  });
};

export const useTransportDataPartners = (
  searchResponse?: SearchResponse,
  interestData?: InterestData,
  uidCookie?: string,
  schedulesResponse?: SchedulesResponse,
  passengers?: PassengerDetails,
  transitMode?: Mode,
  routeIndex?: number,
  isSearchScreen?: boolean,
) => {
  const enabledPartners: DataPartners = Object.keys(interestData ?? {}).filter(
    (key) => interestData?.[key].enabled,
  ) as DataPartners;

  const hasSojernConsent = useRegisterOnConsent(
    SOJERN_CONSENT_PURPOSES_REQUIRED,
    SOJERN_LEGITIMATE_INTEREST_PURPOSES_REQUIRED,
    SOJERN_SALE_CONSENT_REQUIRED,
    SOJERN_SHARING_CONSENT_REQUIRED,
  );

  useAdForm(
    enabledPartners.includes("AdForm") && !!isSearchScreen,
    searchResponse?.analytics?.custom_dimensions,
    searchResponse?.request,
  );

  useAdara({
    searchType: "transport",
    schedulesResponse,
    passengers,
    transitMode,
    routeIndex,
  });

  useStackAdapt({
    searchResponse,
    schedulesResponse,
    passengers,
    transitMode,
    routeIndex,
  });

  useSojernPixel(
    searchResponse?.request,
    enabledPartners.includes("Sojern") && hasSojernConsent,
    uidCookie,
    schedulesResponse?.request,
    passengers,
    transitMode,
  );

  useTravelAudience(
    searchResponse,
    enabledPartners.includes("TravelAudience"),
    schedulesResponse?.request,
    transitMode,
  );

  useGooglePixel(searchResponse, true);
};
